import React, { Suspense, useRef, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  useGLTF,
  OrbitControls,
  Environment,
  PerspectiveCamera,
  AdaptiveDpr,
  AdaptiveEvents,
} from "@react-three/drei";
import { AnimationMixer } from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import playIcon from "./play.svg";
import pauseIcon from "./pause.svg";

const cameraConfig = {
  cape: {
    primitive: [0, -1, 0],
    camera: [-1, 0.5, -2],
  },
  artifact: {
    primitive: [0, -1.2, 0],
    camera: [-1, 1, -3],
  },
  mount: {
    primitive: [0, -0.4, 0],
    camera: [-2, 2, -3],
  },
};

const Model = React.memo(function Model({ path, position, rotation }) {
  const { scene, animations } = useGLTF(path, (loader) => {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("/draco-gltf/");
    loader.setDRACOLoader(dracoLoader);
  });

  const mixer = useRef();
  useEffect(() => {
    if (animations.length) {
      mixer.current = new AnimationMixer(scene);
      animations.forEach((clip) => mixer.current.clipAction(clip).play());
    }
  }, [animations, scene]);

  useFrame((_, delta) => mixer.current?.update(delta));

  return (
    <primitive
      object={scene}
      position={position}
      rotation={rotation}
      dispose={null}
    />
  );
});

export default function CosmeticModel({ item }) {
  const cfg = cameraConfig[item.type] || cameraConfig.artifact;
  const rotation = item.type === "cape" ? [0, Math.PI, 0] : [0, 0, 0];

  const [isRotating, setIsRotating] = useState(false);

  const controlsRef = useRef();

  return (
    <div
      className="cosmetic_model_container grey"
      style={{ position: "relative" }}
    >
      <button
        onClick={() => setIsRotating((r) => !r)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          background: "rgba(255,255,255,0.7)",
          border: "none",
          borderRadius: 4,
          padding: "6px",
          cursor: "pointer",
        }}
      >
        <img
          src={isRotating ? pauseIcon : playIcon}
          alt={isRotating ? "Pause" : "Start"}
          width={12}
          height={12}
        />
      </button>

      <Canvas
        dpr={[1, 1.5]}
        gl={{ antialias: false }}
        frameloop={isRotating ? "always" : "demand"}
      >
        <AdaptiveDpr pixelated />
        <AdaptiveEvents />

        <Suspense fallback={null}>
          <Model
            path={item.model}
            position={cfg.primitive}
            rotation={rotation}
          />

          <PerspectiveCamera makeDefault position={cfg.camera} />

          <OrbitControls
            ref={controlsRef}
            autoRotate={isRotating}
            autoRotateSpeed={1.5}
            enableDamping
            dampingFactor={0.1}
          />

          <Environment
            background={false}
            files="https://app.zeqa.net/cosmetic/hdr.hdr"
          />
        </Suspense>
      </Canvas>
    </div>
  );
}

useGLTF.preload("/models/cape.glb");
useGLTF.preload("/models/artifact.glb");
useGLTF.preload("/models/mount.glb");
